/**
 * Auth API Slice
 *
 * RTK Query API for authentication endpoints including:
 * - OAuth (Google)
 * - Email/Password authentication
 * - Wallet linking
 *
 * @module redux/slices/api/authApi
 */

import { createApi } from "@reduxjs/toolkit/query/react";
import { customBaseQuery } from "../../../utils/general";

export const authApi = createApi({
  reducerPath: "authApi",
  tagTypes: ["Auth", "User", "VerifiedAccounts"],
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    // =========================================================================
    // User Endpoints (moved from userApi for auth-related queries)
    // =========================================================================

    /**
     * Get current user profile
     */
    getUser: builder.query({
      query: () => ({
        url: "/users/me",
      }),
      providesTags: ["User"],
      extraOptions: { maxRetries: 3 },
    }),

    // =========================================================================
    // OAuth & Wallet Linking Endpoints
    // =========================================================================

    /**
     * Get available OAuth providers
     */
    getProviders: builder.query({
      query: () => ({
        url: "/auth/providers",
      }),
    }),

    /**
     * Get wallet linking status
     */
    getWalletStatus: builder.query({
      query: () => ({
        url: "/auth/wallet-status",
      }),
      providesTags: ["Auth"],
    }),

    /**
     * Link wallet to OAuth account (connects wallet for blockchain actions)
     */
    linkWallet: builder.mutation({
      query: ({ body }) => ({
        url: "/auth/connect-wallet",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Auth", "User"],
    }),

    /**
     * Get verified accounts status
     */
    getVerifiedAccounts: builder.query({
      query: () => ({
        url: "/auth/verified-accounts",
      }),
      providesTags: ["VerifiedAccounts"],
    }),

    /**
     * Update verified accounts display settings
     */
    updateVerifiedAccountsDisplay: builder.mutation({
      query: ({ body }) => ({
        url: "/auth/verified-accounts/display",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["VerifiedAccounts"],
    }),

    /**
     * Unlink OAuth provider
     */
    unlinkProvider: builder.mutation({
      query: ({ provider }) => ({
        url: `/auth/link/${provider}`,
        method: "DELETE",
      }),
      invalidatesTags: ["VerifiedAccounts", "User"],
    }),

    /**
     * Refresh OAuth token
     */
    refreshOAuthToken: builder.mutation({
      query: () => ({
        url: "/auth/refresh-token",
        method: "POST",
      }),
    }),

    /**
     * Logout - invalidates the current session
     */
    logout: builder.mutation({
      query: () => ({
        url: "/auth/logout",
        method: "POST",
      }),
      invalidatesTags: ["Auth", "User"],
    }),
  }),
});

// Export hooks
export const {
  // User
  useGetUserQuery,
  useLazyGetUserQuery,
  // OAuth & Linking
  useGetProvidersQuery,
  useGetWalletStatusQuery,
  useLinkWalletMutation,
  useGetVerifiedAccountsQuery,
  useUpdateVerifiedAccountsDisplayMutation,
  useUnlinkProviderMutation,
  useRefreshOAuthTokenMutation,
  // Logout
  useLogoutMutation,
} = authApi;
